import { ButtonHTMLAttributes, forwardRef, ReactNode, useState } from "react";
import classNames from "classnames";
import styles from "@/components/forms/inputs/button/genericButton.module.css";
import { CircularProgress } from "@mui/material";

export type ButtonVariant = "contained" | "outlined" | "icon";

export type ButtonColor =
  | "primary"
  | "secondary"
  | "tertiary"
  | "iconBase"
  | "white"
  | "error";

type ButtonSize = "small" | "medium" | "large";

interface GenericButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  id?: any;
  variant?: ButtonVariant;
  color?: ButtonColor;
  size?: ButtonSize;
  children?: ReactNode;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  disabled?: boolean;
  onClick?: (event: any) => void | Promise<void>;
  className?: string; // for adding custom styles
  type?: "button" | "submit" | "reset";
  waitingForResponse?: boolean; //default false - need to be handed in true for disabling buutton after click
  showSpinnerOnDisable?: boolean;
  loading?: boolean;
}

const GenericButton = forwardRef<HTMLButtonElement, GenericButtonProps>(
  (
    {
      id,
      variant = "contained",
      color = "primary",
      size = "medium",
      children,
      startIcon,
      endIcon,
      disabled = false,
      onClick,
      className,
      type = "button",
      waitingForResponse = false,
      showSpinnerOnDisable = false,
      loading = false,
      ...rest
    },
    ref
  ) => {
    const [waiting, setWaiting] = useState<boolean>(false);

    const handleClick = async (event: any) => {
      if (onClick) {
        if (waitingForResponse) {
          setWaiting(true);
          try {
            const result = onClick(event);

            if (result instanceof Promise) {
              await result;
            }
          } finally {
            setWaiting(false);
          }
        } else {
          onClick(event);
        }
      }
    };

    const isDisabled = disabled || waiting || loading;
    const showSpinner = (waiting || loading) && showSpinnerOnDisable;
    return (
      <button
        ref={ref}
        className={classNames(
          styles.btn,
          styles[`btn--${variant}`],
          styles[`btn--${color}`],
          styles[`btn--${size}`],
          { [styles["btn--disabled"]]: isDisabled },
          className
        )}
        onClick={(event: any) => handleClick(event)}
        disabled={isDisabled}
        type={type}
        id={id}
        {...rest}
      >
        {startIcon && (
          <span
            className={classNames(styles["btn__icon--start"], {
              [styles[`btn__icon--${color}`]]:
                variant === "outlined" && !isDisabled,
              [styles["btn__icon--white"]]:
                variant === "contained" && !isDisabled,
              [styles["btn__icon--disabled"]]: isDisabled,
              [styles["btn--icon"]]: variant === "icon",
            })}
          >
            {startIcon}
          </span>
        )}

        <span className={styles.btn__content}>
          {showSpinner && (
            <span className={styles.btn_disabled_spinnerWrapper}>
              <CircularProgress className={styles.btn_spinner} />
            </span>
          )}

          <span
            className={classNames({
              [styles.btn_disabled_invisibleChildren]: showSpinner,
            })}
          >
            {children}
          </span>
        </span>
        {endIcon && (
          <span
            className={classNames(styles["btn__icon--end"], {
              [styles[`btn__icon--${color}`]]:
                variant === "outlined" && !isDisabled,
              [styles["btn__icon--white"]]:
                variant === "contained" && !isDisabled,
              [styles["btn__icon--disabled"]]: isDisabled,
            })}
          >
            {endIcon}
          </span>
        )}
      </button>
    );
  }
);

GenericButton.displayName = "GenericButton";

export default GenericButton;
